import { icDecisionBasis } from '../ic-decision-basis';
import { icStatisticalInputError } from '../ic-statistical-validation';
import { Component, ElementRef, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { IC_EvaluationResult, IC_ExecutionDetail, IC_Result, IC_Template, IC_TemplateItem } from 'src/app/Interface/IC_Interface';
import { AlertifyService } from 'src/app/Service/Alertify.service';
import { DeviceBehfaService } from 'src/app/Service/DeviceBehfa/DeviceBehfa.service';
import { ICService } from 'src/app/Service/IC/IC.service';

interface ExecutionInput { measured: number | null; reference: number | null; textValue: string; comment: string; readings: string[]; }
interface ExecutionErrors { measured?: string; textValue?: string; readings?: string[]; configuration?: string; }

@Component({ selector: 'app-ic-execution-add', templateUrl: './ICExecutionAdd.component.html', styleUrls: ['./ICExecutionAdd.component.css'] })
export class ICExecutionAddComponent implements OnInit {
  get canEditExecution(): boolean { return this.data?.execution.status === 'IN_PROGRESS'; }
  readonly repeatabilityActivationNotice = 'اجرای عمومی IC-2-REPEATABILITY-V1 تا تصویب روش و سیاست معتبر مسدود است.';
  get repeatabilityBlocked(): boolean { return !!this.data?.items.some(item => !!item.repeatabilityDecision); }
  readonly approvedAttachmentNotice = 'افزودن یا حذف مدارک اجرای تأییدشده مسدود است؛ الحاق رسمی فرآیند جداگانه لازم دارد.';
  readonly decisionBasis = icDecisionBasis;
  data?: IC_ExecutionDetail; template?: IC_Template; equipment: any;
  loading = false; saving = false; completing = false; approving = false; attachmentUploading = false;
  attachmentError = ''; loadError = ''; executionId = 0;
  inputs: { [id: number]: ExecutionInput } = {};
  errors: { [id: number]: ExecutionErrors } = {};
  evaluations: { [id: number]: IC_EvaluationResult } = {};
  actionType = 'TECHNICAL_REVIEW'; actionDecision = ''; actionComment = '';
  completeComment = ''; completeCommentError = ''; observation = ''; deviation = ''; correctiveAction = '';
  attachmentType = 'PHOTO'; attachments: any[] = [];
  selectedAttachmentName = '';
  get attachmentsLocked(): boolean { return this.data?.execution?.status?.toUpperCase() === 'APPROVED'; }

  constructor(private route: ActivatedRoute, private service: ICService, private deviceService: DeviceBehfaService,
    private router: Router, private alert: AlertifyService, private host: ElementRef) {}

  ngOnInit(): void {
    this.executionId = Number(this.route.snapshot.paramMap.get('id'));
    if (!this.executionId) { this.alert.ErrorMessage('شناسه اجرا معتبر نیست'); return; }
    this.load(); this.loadAttachments();
  }

  load(): void {
    this.loading = true; this.loadError = '';
    this.service.getExecution(this.executionId).pipe(finalize(() => this.loading = false)).subscribe({
      next: detail => { this.data = detail; this.initializeInputs(detail); this.loadContext(detail); },
      error: error => {
        this.loadError = error?.error?.message || 'دریافت اطلاعات اجرای کنترل ناموفق بود.';
        this.alert.ErrorMessage(this.loadError);
      }
    });
  }

  private initializeInputs(detail: IC_ExecutionDetail): void {
    (detail.items || []).forEach(item => {
      const previous = detail.results.find(result => result.iC_Template_Item_ID === item.id);
      const input = this.getInput(item.id);
      input.measured = previous?.measured_Value ?? input.measured;
      input.reference = previous?.reference_Value ?? item.reference_Value ?? input.reference;
      input.textValue = previous?.text_Value ?? input.textValue;
      input.comment = previous?.comment ?? input.comment;
      if (previous?.readings) input.readings = previous.readings.map(reading => this.isMeanContract(item) || item.repeatabilityDecision ? reading.exact_Value ?? '' : String(reading.value));
      this.ensureReadings(item);
    });
  }

  private loadContext(detail: IC_ExecutionDetail): void {
    if (detail.template) this.template = detail.template as IC_Template;
    forkJoin({
      equipment: this.deviceService.GetSingle(detail.execution.deviceBehfa_ID).pipe(catchError(() => of(undefined)))
    }).subscribe(context => { this.equipment = context.equipment; });
  }

  getInput(id: number): ExecutionInput {
    return this.inputs[id] || (this.inputs[id] = { measured: null, reference: null, textValue: '', comment: '', readings: [] });
  }

  isRepeat(item: IC_TemplateItem): boolean {
    return this.isMeanContract(item) || item.result_Type === 'REPEATABILITY' || (item.result_Type === 'TRUENESS_BIAS' && Number(item.number_Of_Readings || 0) > 0);
  }
  isMeanContract(item: IC_TemplateItem): boolean { return (this.data?.calculationVersion || 'IC-2') === 'IC-2' && item.result_Type === 'NUMERIC' && item.formula_Type === 'MEAN'; }
  meanValue(itemId: number): string {
    const trace = this.evaluations[itemId]?.calculationTrace || this.data?.results.find(r => r.iC_Template_Item_ID === itemId)?.calculationTrace;
    return trace?.steps.find(s => s.code === 'Mean')?.result || 'پس از ثبت قرائت‌ها محاسبه می‌شود';
  }
  isNumeric(item: IC_TemplateItem): boolean {
    return !this.isMeanContract(item) && (item.result_Type === 'NUMERIC' || item.result_Type === 'NUMERIC_WITH_REFERENCE' || (item.result_Type === 'TRUENESS_BIAS' && !this.isRepeat(item)));
  }
  isObservation(item: IC_TemplateItem): boolean { return item.result_Type === 'CHECK' || item.result_Type === 'PASS_FAIL' || item.result_Type === 'TEXT'; }
  isCompliance(item: IC_TemplateItem): boolean { return item.result_Type === 'CHECK' || item.result_Type === 'PASS_FAIL'; }

  private ensureReadings(item: IC_TemplateItem): void {
    const input = this.getInput(item.id); const count = Math.max(0, Number(item.number_Of_Readings || 0));
    if (this.isMeanContract(item) && input.readings.length > count) return;
    while (input.readings.length < count) input.readings.push('');
    if (input.readings.length > count) input.readings.length = count;
  }

  trackReading(_index: number): number { return _index; }

  onReadingChange(itemId: number, readingIndex: number, value: string): void {
    this.getInput(itemId).readings[readingIndex] = value;
    this.clearError(itemId, 'measured', readingIndex);
  }

  saveResult(item: IC_TemplateItem): void {
    if (item.repeatabilityDecision) { this.alert.ErrorMessage(this.repeatabilityActivationNotice); return; }
    if (!this.canEditExecution) { this.alert.ErrorMessage('ثبت نتیجه فقط برای اجرای در حال انجام مجاز است.'); return; }
    if (this.saving || !this.validateItem(item)) return;
    const input = this.getInput(item.id);
    if (this.isMeanContract(item)) {
      this.saving = true;
      this.service.saveMeanResult(this.executionId, { iC_Template_Item_ID: item.id, comment: input.comment || '',
        readings: input.readings.map((value, index) => ({ reading_No: index + 1, exact_Value: value, unit: item.unit_Title || '' }))
      }).pipe(finalize(() => this.saving = false)).subscribe({
        next: evaluation => { this.evaluations[item.id] = evaluation; this.alert.SuccessMessage(`نتیجه ${evaluation.result} ثبت شد`); this.refreshExecution(); },
        error: error => this.alert.ErrorMessage(error?.error?.message || 'ثبت میانگین ناموفق بود')
      });
      return;
    }
    const readings = input.readings.filter(value => this.hasValue(value) && Number.isFinite(Number(value)))
      .map((value, index) => ({ reading_No: index + 1, value: Number(value), unit: null }));
    const model: Omit<IC_Result, 'result'> = {
      iC_Template_Item_ID: item.id, measured_Value: this.hasValue(input.measured) ? Number(input.measured) : null,
      text_Value: input.textValue || null, comment: input.comment || '', readings
    };
    this.saving = true;
    this.service.saveResult(this.executionId, model).pipe(finalize(() => this.saving = false)).subscribe({
      next: evaluation => { this.evaluations[item.id] = evaluation; this.alert.SuccessMessage(`نتیجه ${evaluation.result} ثبت شد`); this.refreshExecution(); },
      error: error => this.alert.ErrorMessage(error?.error?.message || 'ثبت نتیجه ناموفق بود')
    });
  }

  private validateItem(item: IC_TemplateItem): boolean {
    const input = this.getInput(item.id); const errors: ExecutionErrors = {};
    errors.configuration = icStatisticalInputError(item, input.readings.filter(value =>
      this.hasValue(value) && Number.isFinite(Number(value))).length);
    if (this.isNumeric(item) && !this.hasValue(input.measured)) errors.measured = 'مقدار اندازه‌گیری‌شده الزامی است.';
    else if (this.isNumeric(item) && !Number.isFinite(Number(input.measured))) errors.measured = 'مقدار واردشده معتبر نیست.';
    if (this.isObservation(item) && !this.hasValue(input.textValue))
      errors.textValue = this.isCompliance(item) ? 'ثبت وضعیت انطباق مشاهده‌شده الزامی است.' : 'ثبت مشاهده متنی الزامی است.';
    if (this.isRepeat(item)) {
      errors.readings = input.readings.map(value => !this.hasValue(value) ? 'این قرائت الزامی است.' : !Number.isFinite(Number(value)) ? 'مقدار واردشده معتبر نیست.' : '');
      if (!errors.readings.some(Boolean)) delete errors.readings;
    }
    if (this.isMeanContract(item)) {
      if (!item.number_Of_Readings || input.readings.length !== item.number_Of_Readings || !item.unit_Title) {
        errors.configuration = 'تعداد قرائت و واحد اجرای IC-2 باید مطابق Snapshot باشد.';
      }
      errors.readings = input.readings.map(value => /^[+-]?[0-9]{1,16}(\.[0-9]{1,12})?$/.test(value) ? ''
        : 'قرائت اعشاری دقیق با حداکثر ۱۶ رقم صحیح و ۱۲ رقم اعشار لازم است.');
      if (!errors.readings.some(Boolean)) delete errors.readings;
    }
    this.errors[item.id] = errors;
    const valid = !errors.measured && !errors.textValue && !errors.readings && !errors.configuration;
    if (!valid) setTimeout(() => { const element = this.host.nativeElement.querySelector(`[data-item-id="${item.id}"] .is-invalid`); if (element) element.focus(); });
    return valid;
  }

  clearError(itemId: number, field: 'measured' | 'textValue', readingIndex?: number): void {
    const errors = this.errors[itemId]; if (!errors) return;
    if (field === 'measured') delete errors.measured;
    if (field === 'textValue') delete errors.textValue;
    if (readingIndex !== undefined && errors.readings) errors.readings[readingIndex] = '';
  }

  private refreshExecution(): void {
    this.service.getExecution(this.executionId).subscribe({ next: detail => { this.data = detail; this.initializeInputs(detail); }, error: () => this.alert.ErrorMessage('به‌روزرسانی اطلاعات اجرا ناموفق بود') });
  }

  loadAttachments(): void {
    this.service.getAttachments(this.executionId).subscribe({ next: value => this.attachments = value || [], error: () => this.attachmentError = 'دریافت فهرست پیوست‌ها ناموفق بود.' });
  }
  upload(file: File): void {
    if (!file || this.attachmentUploading) return;
    if (this.attachmentsLocked) { this.attachmentError = 'مدارک اجرای تأییدشده قابل تغییر نیست.'; return; }
    this.attachmentError = ''; this.attachmentUploading = true;
    this.service.uploadAttachment(this.executionId, this.attachmentType, file).pipe(finalize(() => this.attachmentUploading = false)).subscribe({
      next: () => { this.alert.SuccessMessage('فایل پیوست شد'); this.loadAttachments(); },
      error: error => { this.selectedAttachmentName = ''; this.attachmentError = error?.error?.message || 'آپلود فایل ناموفق بود.'; }
    });
  }
  onFile(event: any): void {
    const file = event?.target?.files?.[0];
    if (!file) return;
    this.selectedAttachmentName = file.name;
    this.attachmentError = '';
    this.upload(file);
  }
  downloadAttachment(attachment: any): void {
    this.service.downloadAttachment(attachment.id).subscribe({ next: blob => {
      const url = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = url;
      link.download = attachment.file_Name || 'attachment'; link.click(); URL.revokeObjectURL(url);
    }, error: () => this.attachmentError = 'دانلود فایل ناموفق بود.' });
  }

  result(itemId: number): string { return this.savedResult(itemId)?.result || 'ثبت نشده'; }
  savedResult(itemId: number): IC_Result | undefined { return this.data?.results.find(result => result.iC_Template_Item_ID === itemId); }
  evaluation(itemId: number): IC_EvaluationResult | undefined { return this.evaluations[itemId]; }
  hasFail(): boolean { return (this.data?.results || []).some(result => result.result === 'FAIL'); }
  hasValue(value: any): boolean { return value !== null && value !== undefined && String(value).trim() !== ''; }

  acceptanceSummary(item: IC_TemplateItem): string {
    switch (item.acceptance_Type) {
      case 'MAX': return this.hasValue(item.upper_Limit) ? `حداکثر ${item.upper_Limit}` : 'حداکثر مجاز';
      case 'MIN': return this.hasValue(item.lower_Limit) ? `حداقل ${item.lower_Limit}` : 'حداقل مجاز';
      case 'RANGE': return `از ${item.lower_Limit ?? '-'} تا ${item.upper_Limit ?? '-'}`;
      case 'ABS_DIFFERENCE': return this.hasValue(item.max_Allowed_Difference) ? `اختلاف مطلق حداکثر ${item.max_Allowed_Difference}` : 'حد اختلاف مطلق';
      case 'PERCENT_DIFFERENCE': return this.hasValue(item.max_Allowed_Difference) ? `اختلاف درصدی حداکثر ${item.max_Allowed_Difference}٪` : 'حد اختلاف درصدی';
      case 'STD_DEV': return this.hasValue(item.max_Allowed_Difference) ? `انحراف معیار حداکثر ${item.max_Allowed_Difference}` : 'حد انحراف معیار';
      case 'STATISTICAL_TRUENESS': return 'پذیرش مترولوژیک؛ آمار تکمیلی';
      case 'MANUAL': return item.result_Type === 'TRUENESS_BIAS' ? 'اختلاف در محدوده عدم قطعیت' : 'انطباق مشاهده‌شده';
      default: return item.acceptance_Criteria || '-';
    }
  }

  statusLabel(status: string): string {
    const labels: { [key: string]: string } = { IN_PROGRESS: 'در حال اجرا', COMPLETED: 'تکمیل‌شده', UNDER_REVIEW: 'در انتظار بررسی', APPROVED: 'تأییدشده', FAILED: 'ناموفق', DUE: 'سررسید', WAITING_FOR_APPROVAL: 'در انتظار تأیید' };
    return labels[status] || status || '-';
  }
  sourceLabel(source: string): string { return source === 'SCHEDULED' ? 'برنامه‌ریزی‌شده' : source === 'MANUAL' ? 'دستی' : source || '-'; }
  resultLabel(result: string): string { return result === 'PASS' ? 'قبول' : result === 'FAIL' ? 'رد' : result === 'NA' ? 'غیرقابل اعمال' : result === 'PENDING' ? 'در انتظار' : 'ثبت نشده'; }

  complete(): void {
    if (this.repeatabilityBlocked) { this.alert.ErrorMessage(this.repeatabilityActivationNotice); return; }
    if (!this.canEditExecution) { this.alert.ErrorMessage('این اجرا تکمیل شده یا آماده ثبت نتیجه نیست.'); return; }
    if (this.completing) return;
    if (this.hasFail() && !this.completeComment.trim()) {
      this.completeCommentError = 'برای نتیجه رد، ثبت توضیحات الزامی است.';
      setTimeout(() => { const element = this.host.nativeElement.querySelector('[data-complete-comment]'); if (element) element.focus(); });
      return;
    }
    this.completeCommentError = '';
    this.completing = true;
    this.service.completeExecution(this.executionId, { comment: this.completeComment, observation: this.observation, deviation: this.deviation,
      corrective_Action: this.correctiveAction, action: this.hasFail() ? { action_Type: this.actionType, decision: this.actionDecision, comment: this.actionComment } : null
    }).pipe(finalize(() => this.completing = false)).subscribe({ next: () => { this.alert.SuccessMessage('اجرای کنترل میانی تکمیل شد'); this.load(); }, error: error => this.alert.ErrorMessage(error?.error?.message || 'تکمیل اجرا ناموفق بود') });
  }
  approve(): void {
    if (this.repeatabilityBlocked) { this.alert.ErrorMessage(this.repeatabilityActivationNotice); return; }
    if (this.approving) return; this.approving = true;
    this.service.approveExecution(this.executionId, { action_Type: this.actionType, decision: this.actionDecision, comment: this.actionComment })
      .pipe(finalize(() => this.approving = false)).subscribe({ next: () => { this.alert.SuccessMessage('نتیجه تأیید شد'); this.load(); }, error: error => this.alert.ErrorMessage(error?.error?.message || 'تأیید ناموفق بود') });
  }
  openHistory(): void { if (this.data?.execution.deviceBehfa_ID) this.router.navigate(['/ic/execution/history', this.data.execution.deviceBehfa_ID]); }
  cancel(): void { this.router.navigate(['/ic/due']); }
}
